import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TableList, Typography, TextButton, InfoIcon, useIsMounted } from 'seagull';
import { wait } from "../../../infra/utils";
import { InvoiceAccountingIntegrationStatus } from "../../../components/InvoiceAccountingIntegrationStatus";
import { PayoutAccountingIntegrationStatus } from "../PayoutAccountingIntegrationStatus/PayoutAccountingIntegrationStatus";
import PayoutAccountingSection from "../PayoutsQboSection/PayoutAccountingSection";
import { useAppConfig, useStore, useStoreActions } from "../../../store/store-context";
import { selectors } from "../../../store/state-selectors";
import { getURLWithFullPageInvoice } from "../../../components/FullPageInvoice/FullPageInvoice.utils";
import styles from "./PayoutsDashboardRow.module.scss";
import { timeModel } from "../../../models";
import { compact } from 'lodash-es';
import { getStatusSkin, getStatusText } from "./PayoutsDashboardRow.utils";
import { makeSentryIgnoredError } from "../../../components/ErrorBoundaryPage";
import PayoutFailedCallout from "../PayoutFailureMessage/PayoutFailedCallout";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { Accounting } from "../../../types/api.model";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const POLLING_TIMEOUT = 2 * 60 * 1000; // 2min in ms

function AmountDisplay(props) {
  if (!props.amount) return null;
  return /*#__PURE__*/_jsx(Typography, {
    ellipsis: true,
    skin: "secondary",
    "data-testid": props.testid,
    children: props.amount.display()
  });
}
function FeesCell(_ref) {
  let {
    invoice
  } = _ref;
  return /*#__PURE__*/_jsxs("div", {
    className: styles.fees,
    "data-testid": "fees-".concat(invoice.id),
    children: [/*#__PURE__*/_jsx(AmountDisplay, {
      amount: invoice.totalFees
    }), !invoice.totalFees.isZero() && /*#__PURE__*/_jsx(InfoIcon, {
      "data-testid": "fees-tooltip-".concat(invoice.id),
      info: /*#__PURE__*/_jsxs("div", {
        className: styles.feesTooltip,
        children: [/*#__PURE__*/_jsx(Typography, {
          skin: "invertedStandard",
          weight: "bold",
          children: "Transaction fees"
        }), !invoice.fee.isZero() && /*#__PURE__*/_jsxs("div", {
          "data-testid": "fees-tooltip-anchor-fee-".concat(invoice.id),
          children: [invoice.fee.display(), " Anchor"]
        }), !invoice.processingFee.isZero() && /*#__PURE__*/_jsxs("div", {
          "data-testid": "fees-tooltip-processing-fee-".concat(invoice.id),
          children: [invoice.processingFee.display(), " Credit card"]
        }), !invoice.serviceFeeRefund.isZero() && /*#__PURE__*/_jsxs("div", {
          "data-testid": "fees-tooltip-service-fee-refund-".concat(invoice.id),
          children: [invoice.serviceFeeRefund.neg().display(), " Anchor fee refund"]
        }), !invoice.customerProcessingFee.isZero() && /*#__PURE__*/_jsxs("div", {
          "data-testid": "fees-tooltip-customer-processing-fee-".concat(invoice.id),
          children: [invoice.customerProcessingFee.display(), " Credit card fees returned to the client"]
        }), !invoice.disputeFee.isZero() && /*#__PURE__*/_jsxs("div", {
          "data-testid": "fees-tooltip-dispute-fee-".concat(invoice.id),
          children: [invoice.disputeFee.display(), " chargebacks fee for a dispute received on invoice", ' ', invoice.formattedNumber]
        })]
      })
    })]
  });
}
export default function PayoutsDashboardRow(props) {
  const {
    index,
    invoiceHeaders
  } = props;
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
  const {
    api
  } = useStoreActions();
  const isMounted = useIsMounted();
  const {
    qboPollingInterval
  } = useAppConfig();
  const [hasError, setHasError] = useState(false);
  const testid = "payments-row-".concat(index);
  const {
    XERO_INTEGRATION
  } = useFeatureFlags();
  const [payout, setPayout] = useState(props.payout);
  useEffect(() => setPayout(props.payout), [props.payout]);
  const columnDefs = [{
    width: showQboIntegration || showXeroIntegration ? 160 : 120
  }, {
    width: 80
  }, {
    width: [140, 180]
  }, {
    width: [90, 100]
  }, {
    width: [90, 100]
  }, {
    width: 100,
    hAlign: 'end'
  }, {
    width: 80,
    hAlign: 'end'
  }, {
    width: 110,
    hAlign: 'end'
  }];
  const headerItems = invoiceHeaders.map(text => ({
    value: text
  }));
  async function reloadPayout(accounting) {
    const fetchPayout = async () => {
      return api.payout.getById(payout.id()).catch(err => {
        isMounted() && setHasError(true);
        throw err;
      });
    };
    const start = Date.now();
    let data = await fetchPayout();

    // Keep polling until the invoice reaches a stable integration status.
    while (isMounted() && data.accountingIntegration(accounting) && !data.accountingIntegration(accounting).isStableStatus() && Date.now() - start < POLLING_TIMEOUT) {
      await wait(qboPollingInterval);
      data = await fetchPayout();
    }
    isMounted() && setPayout(data);
    return data;
  }
  if (hasError) {
    throw makeSentryIgnoredError('Payout fetch error');
  }
  return /*#__PURE__*/_jsx(TableList.Row, {
    as: "button",
    "data-testid": testid,
    "data-heapid": "payment",
    rowId: payout.id(),
    items: compact([{
      value: /*#__PURE__*/_jsxs("div", {
        className: styles.payoutIdCell,
        children: [/*#__PURE__*/_jsx(Typography, {
          weight: "light",
          className: styles.payoutId,
          children: payout.formattedNumber()
        }), /*#__PURE__*/_jsxs("div", {
          className: styles.payoutStatus,
          children: [showQboIntegration && /*#__PURE__*/_jsx(PayoutAccountingIntegrationStatus, {
            accounting: Accounting.QBO,
            testid: "".concat(testid, "-qbo"),
            integration: payout.qboIntegration()
          }), showXeroIntegration && XERO_INTEGRATION && /*#__PURE__*/_jsx(PayoutAccountingIntegrationStatus, {
            accounting: Accounting.Xero,
            testid: "".concat(testid, "-xero"),
            integration: payout.xeroIntegration()
          })]
        })]
      })
    }, {
      value: /*#__PURE__*/_jsx(Typography, {
        weight: "light",
        children: payout.formattedCreatedAt()
      })
    }, {
      value: null
    }, {
      value: /*#__PURE__*/_jsx(Typography, {
        weight: "normal",
        capitalize: true,
        "data-testid": "".concat(testid, "-status"),
        skin: getStatusSkin(payout),
        children: getStatusText(payout)
      })
    }, {
      value: /*#__PURE__*/_jsx(AmountDisplay, {
        amount: payout.amount()
      })
    }]),
    expandedContent: () => /*#__PURE__*/_jsxs("div", {
      className: styles.expandedWrapper,
      children: [/*#__PURE__*/_jsx(PayoutFailedCallout, {
        payout: payout,
        testid: "".concat(testid, "-failure-reason")
      }), payout.isPaid() && showQboIntegration && !!payout.qboIntegration() && /*#__PURE__*/_jsx(PayoutAccountingSection, {
        reloadPayout: reloadPayout,
        testid: "".concat(testid, "-qbo-section"),
        payoutId: payout.id(),
        payoutIntegration: payout.qboIntegration(),
        accounting: Accounting.QBO
      }), payout.isPaid() && showXeroIntegration && !!payout.xeroIntegration() && XERO_INTEGRATION && /*#__PURE__*/_jsx(PayoutAccountingSection, {
        reloadPayout: reloadPayout,
        testid: "".concat(testid, "-xero-section"),
        payoutId: payout.id(),
        payoutIntegration: payout.xeroIntegration(),
        accounting: Accounting.Xero
      }), /*#__PURE__*/_jsxs(TableList.Provider, {
        columnDefs: columnDefs,
        variant: "minimal",
        justifyBetween: true,
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.expandedHeader,
          children: /*#__PURE__*/_jsx(TableList.Header, {
            items: headerItems,
            color: "transparent"
          })
        }), /*#__PURE__*/_jsx("div", {
          "data-testid": "invoice-rows",
          children: payout.invoices().map((invoice, idx) => {
            var _payout$qboIntegratio, _payout$xeroIntegrati;
            return /*#__PURE__*/_jsx(TableList.Row, {
              "data-testid": "invoice-row-".concat(invoice.id),
              items: compact([{
                value: /*#__PURE__*/_jsxs("div", {
                  className: styles.invoiceNumber,
                  children: [/*#__PURE__*/_jsx(TextButton, {
                    as: Link,
                    "data-testid": "".concat(testid, "-invoice-link-").concat(idx),
                    "data-heapid": "payment-invoice-link",
                    to: getURLWithFullPageInvoice({
                      invoiceId: invoice.id,
                      relationshipId: invoice.relationshipId
                    }),
                    children: invoice.formattedNumber
                  }), /*#__PURE__*/_jsxs("div", {
                    className: styles.invoiceStatus,
                    children: [showQboIntegration && /*#__PURE__*/_jsx(InvoiceAccountingIntegrationStatus, {
                      testid: "".concat(testid, "-invoice-qbo-").concat(idx),
                      invoiceIntegration: (_payout$qboIntegratio = payout.qboIntegrationForInvoice(invoice.id)) === null || _payout$qboIntegratio === void 0 ? void 0 : _payout$qboIntegratio.integration,
                      accountingType: Accounting.QBO
                    }), showXeroIntegration && XERO_INTEGRATION && /*#__PURE__*/_jsx(InvoiceAccountingIntegrationStatus, {
                      testid: "".concat(testid, "-invoice-xero-").concat(idx),
                      invoiceIntegration: (_payout$xeroIntegrati = payout.xeroIntegrationForInvoice(invoice.id)) === null || _payout$xeroIntegrati === void 0 ? void 0 : _payout$xeroIntegrati.integration,
                      accountingType: Accounting.Xero
                    })]
                  })]
                })
              }, {
                value: invoice.type
              }, {
                value: /*#__PURE__*/_jsx(Typography, {
                  ellipsis: true,
                  skin: "secondary",
                  children: invoice.customer
                })
              }, {
                value: timeModel.formatDate(invoice.createdOn)
              }, {
                value: timeModel.formatDate(invoice.dueDate)
              }, {
                value: /*#__PURE__*/_jsx(AmountDisplay, {
                  amount: invoice.totalAmount
                })
              }, {
                value: /*#__PURE__*/_jsx(FeesCell, {
                  invoice: invoice
                })
              }, {
                value: /*#__PURE__*/_jsx(AmountDisplay, {
                  amount: invoice.depositAmount,
                  testid: "deposit-amount-".concat(invoice.id)
                })
              }])
            }, invoice.id + invoice.type);
          })
        })]
      })]
    })
  });
}